import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOrientation } from './utils/exif';
import { Thumbnail } from './Thumbnail';
import { isSupportedImageFileName } from '@/core/fileFilters';

type Props = {
  files: FileSystemFileHandle[];
  onFiles: (files: File[]) => void;
  picked: Set<number>;
  selected: number | null;
  onToggle: (idx: number) => void;
  onSelect: (idx: number) => void;
};

const MIN           = 200;  // サムネイル 1 辺
const MARGIN_RATIO  = 0.15; // ← ★ 安全領域は画面高さの 30%

type Thumb = { src: string; rotate: string };


export function Gallery({ files, onFiles, picked, selected, onToggle, onSelect }: Props) {
  const [thumbs, setThumbs] = useState<Thumb[]>([]);
  const ref                 = useRef<HTMLDivElement>(null);
  const [dragDepth, setDragDepth] = useState(0);
  const { t } = useTranslation();

  const handleDrop = useCallback(async (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    const dropped: File[] = [];
    const items = Array.from(ev.dataTransfer.items);

    if (items.length && !('getAsFileSystemHandle' in items[0]) && !items[0].webkitGetAsEntry) {
      alert(t('unsupported-dnd'));
      return;
    }

    for (const item of items) {
      if ('getAsFileSystemHandle' in item) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handle = await (item as any).getAsFileSystemHandle?.();
        if (handle) await traverseHandle(handle, dropped);
      } else if (item.webkitGetAsEntry) {
        const entry = item.webkitGetAsEntry();
        if (entry) await traverseEntry(entry, dropped);
      }
    }
    onFiles(dropped);
  }, [onFiles, t]);

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    const next = dragDepth + 1;
    setDragDepth(next);
    if (next === 1) {
      ref.current?.classList.add('drag-in');
      ref.current?.classList.add('drag-over');
    }
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    const next = dragDepth - 1;
    setDragDepth(next);
    if (next <= 0) {
      ref.current?.classList.remove('drag-in');
      ref.current?.classList.remove('drag-over');
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    await handleDrop(e as React.DragEvent<HTMLDivElement>);
    ref.current?.classList.remove('drag-in');
    ref.current?.classList.remove('drag-over');
    setDragDepth(0);
  };

  /* ───────── サムネイル生成 ───────── */
  useEffect(() => {
    setThumbs([]);
    if (!files.length) return;

    let cancelled = false;
    const out: Thumb[] = [];

    (async () => {
      for (const h of files) {
        const file = await h.getFile();

        /* EXIF 回転情報 */
        const o = await getOrientation(file);
        const rotate =
          o === 6 ? 'rotate(90deg)'
        : o === 8 ? 'rotate(-90deg)'
        : o === 3 ? 'rotate(180deg)'
        : 'none';

        out.push({
          src: URL.createObjectURL(file),
          rotate,
        });
      }
      if (!cancelled) setThumbs(out);
    })();

    return () => {
      cancelled = true;
      out.forEach(t => URL.revokeObjectURL(t.src));
    };
  }, [files]);

  /* ───────── 選択変更時: 安全領域付きスクロール ───────── */
  useEffect(() => {
    if (selected === null) return;
    const wrap = ref.current;
    if (!wrap) return;

    const el = wrap.querySelector<HTMLElement>(`[data-idx="${selected}"]`);
    if (!el) return;

    const margin      = wrap.clientHeight * MARGIN_RATIO;
    const viewTop     = wrap.scrollTop;
    const viewBottom  = viewTop + wrap.clientHeight;

    const safeTop     = viewTop    + margin;
    const safeBottom  = viewBottom - margin;
    const elTop       = el.offsetTop;
    const elBottom    = elTop + el.offsetHeight;

    let nextScroll = null;
    if (elTop < safeTop) {
      /* --- 上端に近づいた --- */
      nextScroll = elTop - margin;
    } else if (elBottom > safeBottom) {
      /* --- 下端に近づいた --- */
      nextScroll = elBottom - wrap.clientHeight + margin;
    }

    if (nextScroll !== null) {
      wrap.scrollTo({
        top: Math.max(0, Math.min(nextScroll, wrap.scrollHeight - wrap.clientHeight)),
        behavior: 'smooth',
      });
    }
  }, [selected]);

  /* ───────── 描画 ───────── */
  return (
    <div
      ref={ref}
      className="gallery"
      onDragOver={e => e.preventDefault()}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${MIN}px, 1fr))`,
        gap: 8,
        overflowY: 'auto',
        height: '100%',
      }}
    >
      {files.length === 0 && (
        <div className="gallery-placeholder">{t('gallery-placeholder')}</div>
      )}
      {thumbs.map((t, i) => (
        <div
          key={i}
          data-idx={i}
          className="thumbnail-enter"
          style={{ position: 'relative', display: 'block' }}
        >
          <input
            type="checkbox"
            checked={picked.has(i)}
            onChange={() => onToggle(i)}
            style={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}
          />

          <Thumbnail
            src={t.src}
            rotate={t.rotate}
            file={files[i]}
            picked={picked.has(i)}
            highlight={i === selected}
            onClick={() => onSelect(i)}
          />
        </div>
      ))}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function traverseHandle(handle: any, out: File[]) {
  if (handle.kind === 'file') {
    if (isSupportedImageFileName(handle.name)) {
      out.push(await handle.getFile());
    }
  } else if (handle.kind === 'directory') {
    for await (const h of handle.values()) {
      await traverseHandle(h, out);
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function traverseEntry(entry: any, out: File[]): Promise<void> {
  return new Promise(resolve => {
    if (entry.isFile) {
      if (isSupportedImageFileName(entry.name)) {
        entry.file((f: File) => { out.push(f); resolve(); });
      } else {
        resolve();
      }
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      reader.readEntries(async (entries: any[]) => {
        for (const e of entries) await traverseEntry(e, out);
        resolve();
      });
    }
  });
}
